import React from 'react';
import { Flame } from 'lucide-react';
import { WeightProgressData } from '../../hooks/useWeightProgress';

interface StreaksTileProps {
  data: WeightProgressData | null;
  loading: boolean;
}

/**
 * Streaks Tile - Shows current and best streak from weekly rollups
 *
 * Data comes from the shared useWeightProgress hook.
 * Streak = consecutive weeks with "green" status (weight loss).
 */
export const StreaksTile: React.FC<StreaksTileProps> = ({ data, loading }) => {
  const streak = data?.streak;
  const current = streak?.current ?? 0;
  const best = streak?.best ?? 0;
  const isActive = current > 0;

  if (loading) {
    return (
      <div className="progress-card streaks-tile loading">
        <div className="card-header">
          <Flame size={18} className="card-icon streak-icon" />
          <h3>Streaks</h3>
        </div>
        <div className="card-content">
          <div className="streaks-loading">Loading...</div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="progress-card streaks-tile">
      <div className="card-header">
        <Flame size={18} className="card-icon streak-icon" />
        <h3>Streaks</h3>
      </div>
      <div className="card-content">
        <div className="streak-current">
          <span className={`streak-number ${isActive ? 'active' : 'inactive'}`}>
            {current}
          </span>
          <span className="streak-unit">
            week{current !== 1 ? 's' : ''}
          </span>
          {isActive && <Flame size={20} className="streak-flame" />}
        </div>
        <span className="streak-label">Current Streak</span>
        <div className="streak-best">
          Best: {best} week{best !== 1 ? 's' : ''}
        </div>
      </div>
      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .streaks-tile .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex: 1;
  }

  .streaks-tile.loading .card-content {
    min-height: 100px;
  }

  .streaks-loading {
    font-size: 13px;
    color: var(--text-tertiary);
  }

  .streak-current {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .streak-number {
    font-size: 36px;
    font-weight: 700;
    line-height: 1;
  }

  .streak-number.active {
    color: var(--accent-green);
  }

  .streak-number.inactive {
    color: var(--text-tertiary);
  }

  .streak-unit {
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .streak-flame {
    color: #f59e0b;
    margin-left: 2px;
    align-self: center;
  }

  .streak-label {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .streak-best {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid var(--border-secondary);
    width: 100%;
    text-align: center;
  }
`;

export default StreaksTile;
